import { useVendorNameConstraints } from '@melio/ap-domain';
import { Form, Group, SectionBanner, useBreakpoint } from '@melio/penny';
import { Address, US_STATES } from '@melio/platform-api';
import { FeatureFlags, useFeature } from '@melio/platform-feature-flags';
import { useMelioIntl } from '@melio/platform-i18n';
import { useEffect, useState } from 'react';
import { object, SchemaOf, string } from 'yup';

import { AddressSearchWidget, AddressSearchWidgetProps } from '../../../../../../form-controls';
import { isValidOptionalPhone } from '../../../../../../utils';
import { AddVendorFormFields, AddVendorFormWidgetProps } from '../../../../types';
import { useAddVendorForm } from '../../../../useAddVendorForm';
import {
  useAccountNumberSchema,
  useAddressLine1Schema,
  useAddressLine2Schema,
  useCitySchema,
  useCompanyNameSchema,
  useNicknameSchema,
  useZipCodeSchema,
} from '../../../AddVendorDetailsBaseSchema';
import { CompanyField } from '../../../CompanyField';
import { getCreateVendorPayload } from './utils';

type AddUnmanagedVendorExtendedFormFields = Omit<AddVendorFormFields, 'fullName'>;

const useSchema = ({ showCompanyField }: { showCompanyField: boolean }) => {
  const { formatMessage } = useMelioIntl();
  const companyNameSchema = useCompanyNameSchema({ showCompanyField, isManaged: false });
  const accountNumberSchema = useAccountNumberSchema();
  const nicknameSchema = useNicknameSchema();
  const zipCodeSchema = useZipCodeSchema();
  const AddressLine1Schema = useAddressLine1Schema();
  const AddressLine2Schema = useAddressLine2Schema();
  const citySchema = useCitySchema();

  return object()
    .shape({
      email: string().required().email(formatMessage('widgets.addVendorForm.email.validation.format')),
      phone: string()
        .required()
        .test('valid-phone', formatMessage('widgets.addVendorForm.phone.validation.format'), isValidOptionalPhone)
        .matches(/^[2-9]{1}/, formatMessage('widgets.addVendorForm.phone.validation.beginsWithOne')),
      state: string().required(formatMessage('widgets.addVendorForm.state.validation.required')),
    })
    .concat(AddressLine1Schema)
    .concat(AddressLine2Schema)
    .concat(citySchema)
    .concat(zipCodeSchema)
    .concat(companyNameSchema)
    .concat(nicknameSchema)
    .concat(accountNumberSchema) as SchemaOf<AddUnmanagedVendorExtendedFormFields>;
};

export const AddUnmanagedVendorFormExtended = ({
  onClearCompanyNameSearchField,
  inlineApiErrorCodes,
  onSelectCompanyName,
  onSubmit,
  onSubmissionStateChange,
  isSaving,
  shouldHideCompanyNameField,
  defaultValues,
  ...props
}: AddVendorFormWidgetProps) => {
  const { formatMessage } = useMelioIntl();
  const isManaged = !!props.managed;
  const [isCompanyNameBlurred, setIsCompanyNameBlurred] = useState(false);
  const [isSearchBusinessesInDirectoriesSupported] = useFeature<boolean>(
    FeatureFlags.IsSearchBusinessesInDirectoriesSupported,
    false
  );

  const { formProps, registerField, watch, setValue, reset, formState } =
    useAddVendorForm<AddUnmanagedVendorExtendedFormFields>({
      inlineApiErrorCodes,
      onSubmit,
      getPostVendorPayloadFromFormValues: getCreateVendorPayload,
      schema: useSchema({ showCompanyField: !shouldHideCompanyNameField }),
      defaultValues,
      isSaving,
      onSubmissionStateChange,
    });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const handleAddressChange: AddressSearchWidgetProps['onChange'] = (event) => {
    const address = event.target.value as unknown as Address;

    const setFormFieldValue = (field: keyof Omit<Address, 'countryCode'>) => {
      setValue(field, address ? address[field] : '', {
        shouldValidate: true,
      });
    };
    setFormFieldValue('line1');
    setFormFieldValue('line2');
    setFormFieldValue('city');
    setFormFieldValue('state');
    setFormFieldValue('postalCode');
  };

  const { isExtraSmallScreen } = useBreakpoint();

  const companyNameInputProps = registerField('companyName');

  const { vendorNameBannerDescription, vendorNameConstraintType } = useVendorNameConstraints({
    vendorName: watch('companyName'),
    vendorNameFieldErrorType: companyNameInputProps?.error?.type,
    vendorNameFreeTextInputProps: isSearchBusinessesInDirectoriesSupported
      ? undefined
      : { isBlurred: isCompanyNameBlurred },
    isManaged,
    isFormSubmitted: formState.isSubmitted,
  });

  return (
    <Form data-component="AddVendorFormWidget" {...props} {...formProps} columns={2}>
      {!shouldHideCompanyNameField && (
        <Form.ContentBox colSpan={isExtraSmallScreen ? 1 : 2}>
          <Group spacing="xs" variant="vertical">
            <CompanyField
              colSpan={2}
              onSelectCompanyName={onSelectCompanyName}
              onClearSearchField={() => {
                setValue('companyName', '');
                onClearCompanyNameSearchField?.();
              }}
              onBlur={() => setIsCompanyNameBlurred(true)}
              {...companyNameInputProps}
              autoFocus
            />
            {vendorNameBannerDescription && (
              <SectionBanner
                data-testid={`company-name-suggestion-${vendorNameConstraintType}`}
                description={vendorNameBannerDescription}
              />
            )}
          </Group>
        </Form.ContentBox>
      )}
      <Form.TextField
        colSpan={2}
        {...registerField('nickname')}
        labelProps={{ label: formatMessage('widgets.addVendorForm.nickname.label') }}
        placeholder={formatMessage('widgets.addVendorForm.nickname.placeholder')}
        autoFocus={shouldHideCompanyNameField}
      />
      <Form.TextField
        {...registerField('accountNumber')}
        labelProps={{
          tooltipProps: {
            label: formatMessage('widgets.addVendorForm.accountNumber.labelTooltip'),
          },
          label: formatMessage('widgets.addVendorForm.accountNumber.label'),
        }}
      />
      <Form.TextField
        {...registerField('confirmAccountNumber')}
        labelProps={{
          label: formatMessage('widgets.addVendorForm.confirmAccountNumber.label'),
        }}
      />
      <AddressSearchWidget
        {...registerField('line1')}
        colSpan={2}
        onChange={handleAddressChange}
        // The 'OR' is a workaround for formatting the defaultValue we're getting for this field.
        formatSelectedValue={(option) =>
          (option.value as unknown as Address).line1 || (option.value as unknown as string)
        }
        labelProps={{
          label: formatMessage('widgets.addVendorForm.addressLine1.label'),
        }}
        placeholder={formatMessage('widgets.deliveryMethods.vendorCheckDetailsPayorForm.line1.placeholder')}
      />
      <Form.TextField
        colSpan={2}
        labelProps={{
          label: formatMessage('widgets.addVendorForm.addressLine2.label'),
        }}
        placeholder={formatMessage('widgets.addVendorForm.addressLine2.placeholder')}
        {...registerField('line2')}
      />
      <Form.TextField
        colSpan={2}
        labelProps={{
          label: formatMessage('widgets.addVendorForm.city.label'),
        }}
        {...registerField('city')}
      />
      <Form.DeprecatedSelect
        colSpan={1}
        labelProps={{
          label: formatMessage('widgets.addVendorForm.state.label'),
        }}
        placeholder={formatMessage('widgets.addVendorForm.state.placeholder')}
        {...registerField('state')}
        options={US_STATES.map((state) => ({ value: state, label: formatMessage(`local.USA.states.${state}`) }))}
      />
      <Form.TextField
        colSpan={1}
        labelProps={{
          label: formatMessage('widgets.addVendorForm.postalCode.label'),
        }}
        {...registerField('postalCode')}
      />
      <Form.TextField
        colSpan={2}
        {...registerField('email')}
        labelProps={{ label: formatMessage('widgets.addVendorForm.email.label') }}
        placeholder={formatMessage('widgets.addVendorForm.email.placeholder')}
      />
      <Form.PhoneField
        colSpan={2}
        {...registerField('phone')}
        labelProps={{ label: formatMessage('widgets.addVendorForm.phone.label') }}
        placeholder={formatMessage('widgets.addVendorForm.phone.placeholder')}
      />
    </Form>
  );
};

AddUnmanagedVendorFormExtended.displayName = 'AddVendorFormWidget';
